import React, { useState, useMemo } from "react";
import { DataTable, Badge, Pagination } from "@/components/ui";
import { fmt, fmtDate } from "@/lib/formatters";
import { Eye, Edit2, UserX } from "lucide-react";

export const MaterialExpensesTable = ({ expenses, onView, onEdit, onToggleDisable }) => {
  const [page, setPage] = useState(1);
  const limit = 10;

  const columns = [
    {
      header: "Verve ID",
      accessor: (row) => (
        <span className="text-[10px] uppercase tracking-tight whitespace-nowrap text-brand-blue font-medium">
          {row.customId && row.customId !== '—' ? row.customId : (row.quotationNo || row.expenseId || row.invoiceNo || "—")}
        </span>
      ),
      minWidth: "110px",
    },
    {
      header: 'Customer Info',
      accessor: (row) => {
        const p = row.project || {};
        const fullName = row.clientName || p.customerName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'No Name';
        const phone = row.contactPhone || p.phoneNumber || p.phone || '—';
        const email = row.contactEmail || p.email || '';
        const contactStr = email ? `${phone} | ${email}` : phone;
        
        return (
          <div className="flex flex-col min-w-0" title={`${fullName}\n${contactStr}`}>
            <span className="text-brand-dark tracking-tight text-sm truncate" title={fullName}>{fullName}</span>
            <span className="text-[10px] text-slate-400 truncate mt-0.5" title={contactStr}>
              {phone}{email && <span className="mx-1 text-slate-300">|</span>}{email}
            </span>
          </div>
        );
      },
      minWidth: '200px',
    },
    {
      header: "Address",
      accessor: (row) => (
        <div className="text-slate-600 !text-[10px] truncate max-w-[150px]" title={row.address}>
          {row.address || "—"}
        </div>
      ),
      minWidth: "120px",
    },
    {
      header: "Date",
      accessor: (row) => (
        <div className="text-slate-600 !text-[10px] flex items-center">
          {row.date && row.date !== "—" ? fmtDate(row.date) : "—"}
        </div>
      ),
      minWidth: "90px",
    },
    {
      header: "Category",
      accessor: (row) => {
        let cat = row.category;
        if (!cat) {
          cat = row.type === 'ordering' ? 'ORDERING' : 'FINAL QUOTATION';
        }
        return (
          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded text-brand-blue bg-blue-50 border border-blue-100">
            {cat}
          </span>
        );
      },
      minWidth: "110px",
    },
    {
      header: "Raised By",
      accessor: (row) => (
        <span className="text-[11px] text-slate-600 font-medium">{row.raisedBy || '—'}</span>
      ),
      minWidth: "120px",
    },
    /* 
    {
      header: "Reason",
      accessor: (row) => (
        <span className="text-[11px] text-slate-600 truncate max-w-[120px]" title={row.reason}>
          {row.reason || '—'}
        </span>
      ),
      minWidth: "100px",
    },
    {
      header: "Requester Notes",
      accessor: (row) => (
        <span className="text-[11px] text-slate-600 truncate max-w-[120px]" title={row.requesterNotes}>
          {row.requesterNotes || '—'}
        </span>
      ),
      minWidth: "100px",
    },
    {
      header: "Approver Notes",
      accessor: (row) => (
        <span className="text-[11px] text-slate-600 truncate max-w-[120px]" title={row.approverNotes}>
          {row.approverNotes || '—'}
        </span>
      ),
      minWidth: "100px",
    }, 
    */
    {
      header: "Amount",
      accessor: (row) => (
        <div className="text-rose-500 !text-[10px] font-bold flex items-center">
          {fmt(row.amount || row.totalAmount || 0)}
        </div>
      ),
      minWidth: "110px",
    },
    {
      header: "Status",
      accessor: (row) => (
        <Badge status={row.status || "pending"} />
      ),
      minWidth: "100px",
    },
    {
      header: "Actions",
      accessor: (row) => {
        const isCancelled = row.status === 'cancelled';
        return (
          <div className="flex items-center justify-center">
            <button
              onClick={(e) => { e.stopPropagation(); onView && onView(row); }}
              className="p-1.5 text-slate-400 hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all"
              title="View Detail"
            >
              <Eye size={16} />
            </button>
          </div>
        );
      },
      minWidth: "60px",
    }
  ];

  const getRowClassName = (row) => {
    return row.status === 'cancelled' ? "opacity-50 grayscale saturate-0" : "";
  };

  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return expenses.slice(startIndex, endIndex);
  }, [expenses, page, limit]);

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-sm border border-slate-200">
      <div className="flex-1 min-h-[400px]">
        <DataTable
          columns={columns}
          data={paginatedData}
          onRowClick={(row) => onView && onView(row)}
          getRowClassName={getRowClassName}
          headerRowStyle={{ backgroundColor: '#475569', color: '#fff' }}
          headerCellStyle={{ color: '#fff' }}
        />
      </div>
      
      {expenses.length > 0 && (
        <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-slate-50 rounded-b-lg">
          <div className="text-sm text-slate-500 font-medium">
            {/* Showing <span className="text-slate-700 font-bold">{paginatedData.length}</span> of <span className="text-slate-700 font-bold">{expenses.length}</span> entries */}
          </div>
          <Pagination
            page={page}
            totalPages={Math.ceil(expenses.length / limit)}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
};
